// src/utils/invoiceCreditSummary.js — cnsummary-3e9a6d51 (R1c-3)
// HAND-SYNCED MIRROR of functions/utils/invoiceCreditSummary.js — THE rule for how an invoice that
// carries credit notes is summarised to a customer ("Credit note CN-0009 −50.00 · Net paid 63.00 ·
// Balance Due"). R1d's in-app invoice screen uses this copy so the app, the PDF, the emails and the
// portal say the same thing. Everything below this header is identical to the server file apart
// from the export line; invoiceCreditSummary.test.js compares them. Change both together.

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : 0);

const METHOD_WORDS = { cheque: 'by cheque', etransfer: 'by e-transfer', cash: 'in cash' };

/** The customer's label for one payment-history row (credit-note rows never reach here). */
function paymentLabel(row, numberById) {
  const method = String(row.method || '').trim();
  if (row.isRefund || num(row.amount) < 0) {
    const how = row.stripeRefundId ? 'Refund to card' : (METHOD_WORDS[method.toLowerCase()] ? `Refund ${METHOD_WORDS[method.toLowerCase()]}` : 'Refund');
    const cnNo = row.creditNoteId && numberById[row.creditNoteId];
    return cnNo ? `${how} (credit note ${cnNo})` : how;
  }
  if (method === 'Deposit') return row.reference ? String(row.reference) : 'Deposit';
  return method || 'Payment';
}

/**
 * @param {object} invoice  the invoice doc (paymentHistory, total, amountPaid, balanceDue)
 * @returns {null | { total, credits:[{number, amount, creditNoteId, date}], creditedTotal, amountPaid,
 *                    netPaid, balanceDue, paidInFull, suppressBalanceDue,
 *                    payments:[{date, label, amount, isRefund}] }}
 */
function summarizeCredits(invoice) {
  const inv = invoice || {};
  const history = Array.isArray(inv.paymentHistory) ? inv.paymentHistory : [];
  const cnRows = history.filter((r) => r && r.isCreditNote === true);
  if (!cnRows.length) return null;
  const credits = cnRows.map((r) => ({ number: String(r.reference || ''), amount: r2(Math.abs(num(r.amount))), creditNoteId: r.creditNoteId || null, date: r.date || null }));
  const numberById = {};
  for (const c of credits) if (c.creditNoteId) numberById[c.creditNoteId] = c.number;
  const creditedTotal = r2(credits.reduce((s, c) => s + c.amount, 0));
  const total = r2(num(inv.total));
  // Same amountPaid / balance rule as the plain invoice summary, so the two never disagree.
  const amountPaid = r2(inv.amountPaid || history.reduce((s, p) => s + num(p && p.amount), 0));
  let balanceDue = inv.balanceDue !== undefined ? num(inv.balanceDue) : total - amountPaid;
  if (balanceDue < 0.01) balanceDue = 0;
  const payments = history.filter((r) => r && r.isCreditNote !== true).map((r) => ({
    date: r.date || null, label: paymentLabel(r, numberById), amount: r2(Math.abs(num(r.amount))), isRefund: !!(r.isRefund || num(r.amount) < 0),
  }));
  return {
    total, credits, creditedTotal, amountPaid, netPaid: r2(amountPaid - creditedTotal), balanceDue: r2(balanceDue), paidInFull: balanceDue <= 0.01,
    suppressBalanceDue: inv.source === 'import' && inv.qbStatus === 'partial', payments,
  };
}

/**
 * The summary as plain lines, in order, for any screen: one per credit note (shown with a minus),
 * then "Net paid" (shown WITHOUT a minus), then the balance (unless suppressed).
 * kind: 'credit' | 'netPaid' | 'balance'.
 */
function summaryLines(summary) {
  const lines = summary.credits.map((c) => ({ kind: 'credit', label: `Credit note ${c.number}`, amount: c.amount, creditNoteId: c.creditNoteId }));
  if (summary.netPaid > 0.004) lines.push({ kind: 'netPaid', label: 'Net paid', amount: summary.netPaid });
  if (!summary.suppressBalanceDue) lines.push({ kind: 'balance', label: 'Balance Due', amount: summary.balanceDue, paidInFull: summary.paidInFull });
  return lines;
}

const fmtDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' }) : '';
};

/**
 * Email table rows in the SAME markup as renderInvoiceEmailPaymentSection's own rows, so a
 * credit-note invoice's email looks like every other invoice email. `escapeHtml` is the functions'
 * one email escaper.
 */
function renderCreditSummaryEmail(summary, formatMoney, escapeHtml) {
  const rows = summaryLines(summary).map((l) => {
    if (l.kind === 'credit') return `
                <tr>
                  <td colspan="3" style="text-align: right; padding: 5px; color: #555;">${escapeHtml(l.label)}:</td>
                  <td style="text-align: right; padding: 5px; color: #155724;">-${formatMoney(l.amount)}</td>
                </tr>`;
    if (l.kind === 'netPaid') return `
                <tr>
                  <td colspan="3" style="text-align: right; padding: 5px; color: #555;">Net paid:</td>
                  <td style="text-align: right; padding: 5px;">${formatMoney(l.amount)}</td>
                </tr>`;
    return l.paidInFull ? `
                <tr>
                  <td colspan="3" style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee; color: #155724;">Balance Due:</td>
                  <td style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee; color: #155724;">Paid in full</td>
                </tr>` : `
                <tr>
                  <td colspan="3" style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee;">Balance Due:</td>
                  <td style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee;">${formatMoney(l.amount)}</td>
                </tr>`;
  }).join('');
  const paymentsListHtml = summary.payments.length ? `
          <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
              <tr style="background-color: #f2f2f2; text-align: left;">
                <th style="padding: 10px;">Payments and Refunds</th>
                <th style="padding: 10px;"></th>
                <th style="padding: 10px; text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${summary.payments.map((p) => `
              <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 10px; color: #555;">${fmtDate(p.date)}</td>
                <td style="padding: 10px;">${escapeHtml(p.label)}</td>
                <td style="padding: 10px; text-align: right;">${p.isRefund ? '-' : ''}${formatMoney(p.amount)}</td>
              </tr>`).join('')}
            </tbody>
          </table>` : '';
  return { paidRowsHtml: rows, paymentsListHtml };
}

/** The invoice PDF's version (generateInvoiceHtml markup and classes). */
function renderCreditSummaryPdf(summary, formatCurrency, escapeHtml) {
  const rows = summaryLines(summary).map((l) => {
    if (l.kind === 'credit') return `
                    <tr>
                        <td colspan="3" style="text-align: right; padding: 5px; color: #555;">${escapeHtml(l.label)}:</td>
                        <td style="text-align: right; padding: 5px; color: #155724;">-${formatCurrency(l.amount)}</td>
                    </tr>`;
    if (l.kind === 'netPaid') return `
                    <tr>
                        <td colspan="3" style="text-align: right; padding: 5px; color: #555;">Net paid:</td>
                        <td style="text-align: right; padding: 5px;">${formatCurrency(l.amount)}</td>
                    </tr>`;
    return l.paidInFull ? `
                    <tr>
                        <td colspan="3" style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee; color: #155724;">Balance Due:</td>
                        <td style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee; color: #155724;">Paid in full</td>
                    </tr>` : `
                    <tr>
                        <td colspan="3" style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee;">Balance Due:</td>
                        <td style="text-align: right; padding: 10px; font-weight: bold; border-top: 2px solid #eee;">${formatCurrency(l.amount)}</td>
                    </tr>`;
  }).join('');
  const paymentsListHtml = summary.payments.length ? `
            <table class="items-table" style="margin-top: 20px;">
                <thead>
                    <tr>
                        <th style="width: 40%;">Payments and Refunds</th>
                        <th style="width: 40%;"></th>
                        <th class="text-right" style="width: 20%;">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${summary.payments.map((p) => `
                    <tr class="item">
                        <td style="color: #555;">${fmtDate(p.date)}</td>
                        <td>${escapeHtml(p.label)}</td>
                        <td class="text-right">${p.isRefund ? '-' : ''}${formatCurrency(p.amount)}</td>
                    </tr>`).join('')}
                </tbody>
            </table>` : '';
  return { paidRowsHtml: rows, paymentsListHtml };
}

/**
 * What the PORTAL may see (getCustomerInvoices): the summary with only customer-safe fields.
 * creditNoteId is kept so the portal can ask downloadPortalCreditNotePdf for the note
 * (that function checks the note is the signed-in customer's own).
 */
function portalCreditSummary(summary) {
  return {
    total: summary.total, creditedTotal: summary.creditedTotal, netPaid: summary.netPaid, balanceDue: summary.balanceDue,
    paidInFull: summary.paidInFull, suppressBalanceDue: summary.suppressBalanceDue,
    credits: summary.credits.map((c) => ({ number: c.number, amount: c.amount, creditNoteId: c.creditNoteId, date: c.date })),
    payments: summary.payments.map((p) => ({ date: p.date, label: p.label, amount: p.amount, isRefund: p.isRefund })),
    lines: summaryLines(summary),
  };
}

export { summarizeCredits, summaryLines, paymentLabel, renderCreditSummaryEmail, renderCreditSummaryPdf, portalCreditSummary };
